import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWebSocket } from "@/hooks/useWebSocket";
import {
  Activity,
  X,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Circle,
  Loader2,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import {
  PipelineProgressMessage,
  PipelineStepInfo,
} from "@/contexts/WebSocketTypes";
import { logger } from "@/utils/logger";

interface ModalAnalysisProps {
  modalId: string;
  isOpen: boolean;
  onClose: () => void;
  apiBaseUrl: string;
}

// Pipeline steps configuration matching backend
const PIPELINE_STEPS = [
  {
    key: "fetch",
    display_name: "Intercom Fetch",
    description: "Fetch latest articles from Intercom",
    api_key: "run_fetch",
  },
  {
    key: "preprocess",
    display_name: "Preprocessing",
    description: "Run parsing and processing",
    api_key: "run_preprocess",
  },
  {
    key: "embeddings",
    display_name: "Embedding Generation",
    description: "Generate embeddings for content",
    api_key: "run_embeddings",
  },
  {
    key: "analyze",
    display_name: "Similarity Analysis",
    description: "Run similarity analysis",
    api_key: "run_analyze",
  },
  {
    key: "prepare_queries",
    display_name: "Prepare AI Queries",
    description: "Prepare AI analysis queries",
    api_key: "run_prepare_queries",
  },
  {
    key: "execute_queries",
    display_name: "Execute AI Queries",
    description: "Execute AI analysis queries",
    api_key: "run_execute_queries",
  },
  {
    key: "populate_db",
    display_name: "Populate Database",
    description: "Populate MongoDB database",
    api_key: "run_populate_db",
  },
  {
    key: "generate_missing",
    display_name: "Generate Missing Articles",
    description: "Generate missing article content",
    api_key: "run_generate_missing",
  },
];

interface PipelineTriggerOptions {
  run_fetch?: boolean;
  use_optimized_fetch?: boolean;
  run_preprocess?: boolean;
  run_embeddings?: boolean;
  run_analyze?: boolean;
  run_prepare_queries?: boolean;
  run_execute_queries?: boolean;
  run_populate_db?: boolean;
  run_generate_missing?: boolean;
  run_all?: boolean;
}

interface PipelineRunStep {
  step_name: string;
  status: string;
  message?: string;
  progress?: number;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  error?: string;
  timestamp: string;
}

interface PipelineRunLog {
  level: string;
  message: string;
  timestamp: string;
  source: string;
}

interface PipelineRunHistoryItem {
  id: string;
  user_id: string;
  username: string;
  arguments: string[];
  triggered_at: string;
  status: string;
  current_step?: string;
  steps: PipelineRunStep[];
  logs: PipelineRunLog[];
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  exit_code?: number;
  error?: string;
}

interface PipelineHistoryResponse {
  runs: PipelineRunHistoryItem[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export function ModalAnalysis({
  modalId,
  isOpen,
  onClose,
  apiBaseUrl,
}: ModalAnalysisProps) {
  const { token } = useAuth();
  const { lastMessage } = useWebSocket();

  // Pipeline state
  const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());
  const [stepStates, setStepStates] = useState<
    Record<string, PipelineStepInfo>
  >({});
  const [isRunning, setIsRunning] = useState(false);
  const [currentPipelineId, setCurrentPipelineId] = useState<string | null>(
    null,
  );
  const [logs, setLogs] = useState<
    Array<{
      timestamp: string;
      message: string;
      level: string;
    }>
  >([]);
  const [error, setError] = useState<string | null>(null);

  // Pipeline history state
  const [pipelineHistory, setPipelineHistory] = useState<
    PipelineRunHistoryItem[]
  >([]);
  const [selectedHistoryRun, setSelectedHistoryRun] = useState<string>("new");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(false);

  // Initialize step states
  useEffect(() => {
    const initialStates: Record<string, PipelineStepInfo> = {};
    PIPELINE_STEPS.forEach((step) => {
      initialStates[step.key] = {
        step: step.key,
        display_name: step.display_name,
        status: "pending",
        message: step.description,
      };
    });
    setStepStates(initialStates);
  }, []);

  // Handle WebSocket pipeline progress messages
  useEffect(() => {
    if (!lastMessage) return;

    try {
      const messageData = JSON.parse(lastMessage.data);

      if (messageData.type === "pipeline_progress") {
        const progressMessage = messageData as PipelineProgressMessage;

        // Only process WebSocket messages for new/live pipelines when not viewing historical data
        // or if this message is for a pipeline we're currently tracking
        if (
          isViewingHistory &&
          progressMessage.pipeline_id !== currentPipelineId
        ) {
          logger.debug(
            `Ignoring WebSocket update for pipeline ${progressMessage.pipeline_id} while viewing historical data for ${currentPipelineId}`,
          );
          return;
        }

        // Update current pipeline ID if this is a new pipeline and we're not viewing history
        if (
          !isViewingHistory &&
          progressMessage.pipeline_id &&
          progressMessage.pipeline_id !== currentPipelineId
        ) {
          setCurrentPipelineId(progressMessage.pipeline_id);
          setLogs([]);
        }

        // Only update state if this is for our current pipeline or we're in live mode
        if (
          !isViewingHistory ||
          progressMessage.pipeline_id === currentPipelineId
        ) {
          // Update step state
          setStepStates((prev) => ({
            ...prev,
            [progressMessage.step]: {
              ...prev[progressMessage.step],
              status: progressMessage.status,
              message: progressMessage.message,
              progress: progressMessage.progress,
              error: progressMessage.error,
            },
          }));

          // Add to logs
          setLogs((prev) =>
            [
              ...prev,
              {
                timestamp: new Date(
                  progressMessage.timestamp,
                ).toLocaleTimeString(),
                message: progressMessage.message,
                level: progressMessage.status === "error" ? "error" : "info",
              },
            ].slice(-100),
          ); // Keep last 100 entries

          // Update running state
          if (
            progressMessage.status === "started" ||
            progressMessage.status === "running"
          ) {
            setIsRunning(true);
          } else if (
            progressMessage.status === "completed" &&
            progressMessage.step === "execute"
          ) {
            setIsRunning(false);
          } else if (progressMessage.status === "error") {
            setIsRunning(false);
          }

          logger.debug("Pipeline progress update:", progressMessage);
        }
      }
    } catch (error) {
      logger.error("Failed to parse WebSocket message:", error);
    }
  }, [lastMessage, currentPipelineId, isViewingHistory]);

  // Fetch pipeline history
  const fetchPipelineHistory = useCallback(async () => {
    if (!token) return;

    setHistoryLoading(true);
    try {
      const response = await axios.get<PipelineHistoryResponse>(
        `${apiBaseUrl}/pipeline/history?page=1&page_size=50`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPipelineHistory(response.data.runs);
      logger.info("Fetched pipeline history:", response.data);
    } catch (error) {
      logger.error("Failed to fetch pipeline history:", error);
      // Don't show error for history fetch failure as it's not critical
    } finally {
      setHistoryLoading(false);
    }
  }, [token, apiBaseUrl]);

  // Reset state when modal opens and fetch pipeline history
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setCurrentPipelineId(null);
      setLogs([]);
      setSelectedHistoryRun("new");
      setIsViewingHistory(false);

      // Reset step states to pending
      const resetStates: Record<string, PipelineStepInfo> = {};
      PIPELINE_STEPS.forEach((step) => {
        resetStates[step.key] = {
          step: step.key,
          display_name: step.display_name,
          status: "pending",
          message: step.description,
        };
      });
      setStepStates(resetStates);

      // Fetch pipeline history
      fetchPipelineHistory();
    }
  }, [isOpen, fetchPipelineHistory]);

  // Handle history run selection
  const handleHistoryRunSelect = useCallback(
    (runId: string) => {
      logger.info(`Selecting pipeline run: ${runId}`);

      if (runId === "new") {
        logger.info("Resetting to new pipeline mode");
        setSelectedHistoryRun("new");
        setIsViewingHistory(false);
        setLogs([]);
        setCurrentPipelineId(null);
        setError(null);

        // Reset step states to pending
        const resetStates: Record<string, PipelineStepInfo> = {};
        PIPELINE_STEPS.forEach((step) => {
          resetStates[step.key] = {
            step: step.key,
            display_name: step.display_name,
            status: "pending",
            message: step.description,
          };
        });
        setStepStates(resetStates);

        // Clear selected steps
        setSelectedSteps(new Set());
        return;
      }

      const selectedRun = pipelineHistory.find((run) => run.id === runId);
      if (!selectedRun) {
        logger.error(`Pipeline run not found: ${runId}`);
        return;
      }

      logger.info(`Found pipeline run:`, selectedRun);

      // Clear existing state first to prevent old data from persisting
      setLogs([]);
      setError(null);

      // Set viewing mode and identifiers
      setSelectedHistoryRun(runId);
      setIsViewingHistory(true);
      setCurrentPipelineId(runId);

      // Parse arguments to determine selected steps
      const argumentsToSteps = new Set<string>();
      selectedRun.arguments.forEach((arg) => {
        const step = PIPELINE_STEPS.find(
          (s) => `--${s.key.replace("_", "-")}` === arg,
        );
        if (step) {
          argumentsToSteps.add(step.key);
        }
        // Handle special case for --fetch vs run_fetch
        if (arg === "--fetch") argumentsToSteps.add("fetch");
        if (arg === "--use-optimized-fetch")
          argumentsToSteps.add("use_optimized_fetch");
        if (arg === "--preprocess") argumentsToSteps.add("preprocess");
        if (arg === "--embeddings") argumentsToSteps.add("embeddings");
        if (arg === "--analyze") argumentsToSteps.add("analyze");
        if (arg === "--prepare-queries")
          argumentsToSteps.add("prepare_queries");
        if (arg === "--execute-queries")
          argumentsToSteps.add("execute_queries");
        if (arg === "--populate-db") argumentsToSteps.add("populate_db");
        if (arg === "--generate-missing")
          argumentsToSteps.add("generate_missing");
      });

      logger.info(`Setting selected steps:`, Array.from(argumentsToSteps));
      setSelectedSteps(argumentsToSteps);

      // Update step states from historical run
      const historicalStepStates: Record<string, PipelineStepInfo> = {};
      PIPELINE_STEPS.forEach((step) => {
        const runStep = selectedRun.steps.find((s) => s.step_name === step.key);
        const validStatuses = [
          "pending",
          "started",
          "in_progress",
          "running",
          "completed",
          "error",
        ];
        const stepStatus =
          runStep?.status && validStatuses.includes(runStep.status)
            ? (runStep.status as PipelineStepInfo["status"])
            : "pending";

        historicalStepStates[step.key] = {
          step: step.key,
          display_name: step.display_name,
          status: stepStatus,
          message: runStep?.message || step.description,
          progress: runStep?.progress,
          error: runStep?.error,
          started_at: runStep?.started_at,
          completed_at: runStep?.completed_at,
        };
      });
      setStepStates(historicalStepStates);

      // Update logs from historical run
      const historicalLogs = selectedRun.logs.map((log) => ({
        timestamp: new Date(log.timestamp).toLocaleTimeString(),
        message: log.message,
        level: log.level.toLowerCase() === "error" ? "error" : "info",
      }));
      setLogs(historicalLogs);

      logger.info(`Historical viewing mode activated for run: ${runId}`);
    },
    [pipelineHistory], // Only pipelineHistory is needed since we're using state setters
  );

  // Handle step selection
  const toggleStep = useCallback(
    (stepKey: string) => {
      if (isRunning || isViewingHistory) return; // Don't allow changes while running or viewing history

      setSelectedSteps((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(stepKey)) {
          newSet.delete(stepKey);
        } else {
          newSet.add(stepKey);
        }
        return newSet;
      });
    },
    [isRunning, isViewingHistory],
  );

  // Trigger pipeline
  const triggerPipeline = useCallback(async () => {
    if (!token) {
      setError("Authentication token not found. Please login.");
      return;
    }

    if (isViewingHistory) {
      setError(
        "Cannot run pipeline while viewing historical data. Select 'New' to create a new run.",
      );
      return;
    }

    if (selectedSteps.size === 0) {
      setError("Please select at least one pipeline step.");
      return;
    }

    setIsRunning(true);
    setError(null);

    try {
      // Build the payload
      const payload: PipelineTriggerOptions = {};

      // Map selected steps to API keys
      PIPELINE_STEPS.forEach((step) => {
        if (selectedSteps.has(step.key)) {
          payload[step.api_key as keyof PipelineTriggerOptions] = true;
        }
      });

      // If fetch is selected, always use optimized fetch
      if (selectedSteps.has("fetch")) {
        payload.use_optimized_fetch = true;
      }

      logger.info("Triggering pipeline with payload:", payload);

      const response = await axios.post(`${apiBaseUrl}/pipeline/run`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 202) {
        logger.info("Pipeline triggered successfully:", response.data);
      }
    } catch (error) {
      logger.error("Failed to trigger pipeline:", error);
      setError(
        `Failed to trigger pipeline: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      setIsRunning(false);
    }
  }, [token, selectedSteps, apiBaseUrl]);

  // Get step icon based on status
  const getStepIcon = (status: string, isSelected: boolean) => {
    if (status === "completed") {
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    } else if (status === "error") {
      return <XCircle className="h-5 w-5 text-error" />;
    } else if (status === "started" || status === "running") {
      return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    } else if (isSelected) {
      return <Clock className="h-5 w-5 text-warning" />;
    } else {
      return <Circle className="h-5 w-5 text-base-content/30" />;
    }
  };

  // Get step styling based on status
  const getStepStyling = (status: string, isSelected: boolean) => {
    if (status === "completed") {
      return "border-success bg-success/10";
    } else if (status === "error") {
      return "border-error bg-error/10";
    } else if (status === "started" || status === "running") {
      return "border-primary bg-primary/10";
    } else if (isSelected) {
      return "border-warning bg-warning/10";
    } else {
      return "border-base-300 bg-base-100 text-base-content/70 hover:border-primary/50";
    }
  };

  return (
    <dialog id={modalId} className="modal" open={isOpen}>
      <div className="modal-box max-h-[90vh] w-11/12 max-w-6xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-3">
              <Activity className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-base-content">
                Pipeline Analysis
              </h3>
              <p className="text-sm text-base-content/70">
                Select and run data processing pipeline steps
              </p>
            </div>
          </div>
          {/* Pipeline History Selector */}
          <div className="ml-4">
            <div className="flex items-center gap-3">
              <div className="dropdown">
                <div
                  tabIndex={isRunning ? -1 : 0}
                  role="button"
                  className={`btn min-w-[250px] justify-between btn-outline btn-sm ${
                    isRunning ? "btn-disabled cursor-not-allowed" : ""
                  }`}
                  onClick={(e) => {
                    if (isRunning) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <span className="truncate">
                    {selectedHistoryRun === "new"
                      ? "New Pipeline Run"
                      : (() => {
                          const selectedRun = pipelineHistory.find(
                            (run) => run.id === selectedHistoryRun,
                          );
                          if (selectedRun) {
                            return `${new Date(selectedRun.triggered_at).toLocaleDateString()} ${new Date(selectedRun.triggered_at).toLocaleTimeString()} - ${
                              selectedRun.status === "completed"
                                ? "✅"
                                : selectedRun.status === "failed"
                                  ? "❌"
                                  : "⏳"
                            } ${selectedRun.arguments.join(" ")}`;
                          }
                          return "Select Pipeline Run";
                        })()}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                </div>
                <ul
                  tabIndex={0}
                  className="dropdown-content menu z-[100] max-h-80 w-96 flex-nowrap overflow-x-hidden overflow-y-auto rounded-box border border-base-300 bg-base-100 p-2 shadow-lg"
                >
                  <li>
                    <a
                      className={`${
                        selectedHistoryRun === "new" ? "active" : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isRunning) {
                          handleHistoryRunSelect("new");
                          // Close dropdown
                          if (document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur();
                          }
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 flex-shrink-0 rounded-full bg-success" />
                        <span>New Pipeline Run</span>
                      </div>
                    </a>
                  </li>
                  {pipelineHistory.length > 0 && (
                    <li>
                      <div className="divider my-1"></div>
                    </li>
                  )}
                  {pipelineHistory.map((run) => (
                    <li key={run.id}>
                      <a
                        className={`${
                          selectedHistoryRun === run.id ? "active" : ""
                        } text-xs`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isRunning) {
                            handleHistoryRunSelect(run.id);
                            // Close dropdown
                            if (document.activeElement instanceof HTMLElement) {
                              document.activeElement.blur();
                            }
                          }
                        }}
                      >
                        <div className="flex w-full items-start gap-2">
                          <div
                            className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${
                              run.status === "completed"
                                ? "bg-success"
                                : run.status === "failed"
                                  ? "bg-error"
                                  : "bg-warning"
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">
                              {new Date(run.triggered_at).toLocaleDateString()}{" "}
                              {new Date(run.triggered_at).toLocaleTimeString()}
                            </div>
                            <div className="truncate text-xs opacity-70">
                              {run.arguments.join(" ")} • {run.username}
                            </div>
                            <div className="text-xs opacity-50">
                              Duration:{" "}
                              {run.duration_seconds
                                ? `${Math.round(run.duration_seconds)}s`
                                : "N/A"}
                            </div>
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              {historyLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-base-content/60" />
              )}
            </div>
            {isViewingHistory && (
              <div className="mt-2 flex items-center gap-2 text-sm text-base-content/70"></div>
            )}
          </div>
          <button
            onClick={onClose}
            className="btn btn-circle btn-ghost btn-sm"
            disabled={isRunning}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="flex min-h-[400px] gap-6">
          {/* Left Side - Pipeline Steps Timeline */}
          <div className="w-1/2">
            {error && (
              <div className="mb-4 alert alert-error">
                <AlertTriangle className="h-5 w-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Timeline Steps */}
            <div className="space-y-3">
              {PIPELINE_STEPS.map((step, index) => {
                const stepState = stepStates[step.key];
                const isSelected = selectedSteps.has(step.key);
                const isLast = index === PIPELINE_STEPS.length - 1;

                return (
                  <div key={step.key} className="relative">
                    {/* Step Card */}
                    <div
                      className={`relative rounded-lg border-2 p-4 text-xs transition-all duration-200 ${getStepStyling(stepState?.status || "pending", isSelected)} ${
                        isRunning ||
                        isViewingHistory ||
                        ((step.key === "execute_queries" ||
                          step.key === "populate_db" ||
                          step.key === "generate_missing") &&
                          stepState?.status !== "completed")
                          ? "cursor-not-allowed"
                          : "cursor-pointer hover:shadow-md"
                      } `}
                      onClick={() =>
                        !isRunning &&
                        !isViewingHistory &&
                        (step.key === "fetch" ||
                          step.key === "preprocess" ||
                          step.key === "embeddings" ||
                          step.key === "analyze" ||
                          step.key === "prepare_queries") &&
                        stepState?.status !== "completed" &&
                        toggleStep(step.key)
                      }
                    >
                      <div className="flex items-start gap-3">
                        {/* Step Icon */}
                        <div className="mt-0.5 flex-shrink-0">
                          {getStepIcon(
                            stepState?.status || "pending",
                            isSelected,
                          )}
                        </div>

                        {/* Step Content */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h5 className="truncate font-medium">
                              {step.display_name}
                            </h5>
                            {stepState?.progress !== undefined &&
                              stepState.progress > 0 && (
                                <span className="text-xs text-base-content/60">
                                  {stepState.progress}%
                                </span>
                              )}
                          </div>
                          <p className="mt-1 text-xs opacity-75">
                            {stepState?.message || step.description}
                          </p>
                          {stepState?.error && (
                            <p className="mt-1 text-xs text-error">
                              Error: {stepState.error}
                            </p>
                          )}
                        </div>

                        {/* Selection Indicator */}
                        {isSelected && (
                          <div className="flex-shrink-0">
                            <div className="h-2 w-2 rounded-full bg-current opacity-60" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timeline Connector */}
                    {!isLast && (
                      <div className="absolute top-full left-6 h-3 w-0.5 bg-base-300" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Logs */}
          <div className="w-1/2">
            <h4 className="mb-4 text-lg font-semibold">Pipeline Logs</h4>

            <div className="h-[400px] overflow-hidden rounded-lg bg-base-200 p-4">
              {logs.length === 0 ? (
                <div className="flex h-full items-center justify-center text-base-content/50">
                  <div className="text-center">
                    <Activity className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    <p>Pipeline logs will appear here when running</p>
                  </div>
                </div>
              ) : (
                <div className="h-full space-y-1 overflow-y-auto font-mono text-sm">
                  {logs.map((log, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${
                        log.level === "error"
                          ? "text-error"
                          : "text-base-content"
                      }`}
                    >
                      <span className="flex-shrink-0 text-base-content/60">
                        {log.timestamp}
                      </span>
                      <span className="break-all">{log.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="modal-action mt-2">
          <div className="flex w-full justify-between gap-3">
            <button
              onClick={onClose}
              className="btn btn-ghost"
              disabled={isRunning}
            >
              {isRunning ? "Running..." : "Close"}
            </button>

            <div className="flex gap-2">
              <button
                onClick={triggerPipeline}
                disabled={
                  isRunning || isViewingHistory || selectedSteps.size === 0
                }
                className="btn btn-primary"
                title={
                  isViewingHistory
                    ? "Viewing historical data - cannot run pipeline"
                    : undefined
                }
              >
                {isRunning ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <BarChart3 className="mr-2 h-4 w-4" />
                )}
                Run Selected Steps
              </button>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
}
